import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import { PrizeDistributionModalTabs } from '../../../../types';
import type { GameTypeName } from '../../../../types/Game';
import { GameType } from '../../../../types/Game';
import GameCard from '../GameCard/GameCard';
import HowToPlayButton from '../HowToPlayButton/HowToPlayButton';
import PlayersToggle from '../PlayersToggle/PlayersToggle';

import GameCardContent from './GameCardContent';
import { TXTCLI_Buy_In, TXTCLI_Players_42 } from './GameCards.messages.json';
import styles from './GameCards.scss';

export interface GameCardsProps {
  tab: GameTypeName;
  buyIn: string;
  players?: number;
  highBet: number;
  lowBet: number;
  timeBank: number;
  chip: number;
  selectedTab: PrizeDistributionModalTabs;
  playerCount: number[];
  weights: number[];
}

const GameCards: React.FC<GameCardsProps> = ({
  tab,
  buyIn,
  players,
  highBet,
  lowBet,
  timeBank,
  chip,
  selectedTab,
  playerCount,
  weights
}) => {
  const { formatMessage } = useIntl();
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const { greaterThan } = useScreenSize();
  const isMobile = useSelector(select.device.isMobile);
  const smallScreen = !greaterThan(Size.Small);
  const isSmallScreen = isMobile || smallScreen;
  const isHoldemMax = tab === GameType.HoldemMax;
  const isHoldemMaxMobile = isHoldemMax && isSmallScreen;
  const prizeTabSelected = selectedTab === PrizeDistributionModalTabs.prizes;
  const isInformationTabSelected = selectedTab === PrizeDistributionModalTabs.information;

  return (
    <div data-testid="prize-distribution-game-cards">
      {isInformationTabSelected && (
        <div className={styles.buttonContainer}>
          <HowToPlayButton gameType={tab} />
        </div>
      )}
      <div className={styles.gameCards}>
        {isHoldemMax && !isSmallScreen && (
          <>
            <PlayersToggle weights={weights} numPlayersList={playerCount} />
            <GameCardContent
              tab={tab}
              chip={chip}
              highBet={highBet}
              lowBet={lowBet}
              timeBank={timeBank}
              buyIn={buyIn}
              isPlayMoney={isPlayMoney}
              isSmallScreen={isSmallScreen}
            />
          </>
        )}
        {isHoldemMaxMobile && prizeTabSelected && (
          <PlayersToggle weights={weights} numPlayersList={playerCount} />
        )}
        {isHoldemMaxMobile && !prizeTabSelected && (
          <div className={styles.cardsContainer}>
            <GameCardContent
              tab={tab}
              chip={chip}
              highBet={highBet}
              lowBet={lowBet}
              timeBank={timeBank}
              buyIn={buyIn}
              isPlayMoney={isPlayMoney}
              isSmallScreen={isSmallScreen}
            />
          </div>
        )}
        {!isHoldemMax && (
          <div className={styles.cardsContainer}>
            <GameCard
              className={isPlayMoney ? 'BuyInPlay' : 'BuyInReal'}
              label={formatMessage(TXTCLI_Buy_In)}
              value={buyIn}
              dataTestId="prize-distribution-game-card-buy-in"
            />
            <GameCard
              className="Players"
              label={formatMessage(TXTCLI_Players_42)}
              value={players || '-'}
              dataTestId="prize-distribution-game-card-players"
            />
            <GameCardContent
              tab={tab}
              chip={chip}
              highBet={highBet}
              lowBet={lowBet}
              timeBank={timeBank}
              buyIn={buyIn}
              isPlayMoney={isPlayMoney}
              isSmallScreen={isSmallScreen}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GameCards;
